import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aws } from '../lib/awsdb';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const SERIF = "'Tiempos', Georgia, 'Times New Roman', serif";
const MONO  = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthKey(year, m) { return `${year}-${String(m).padStart(2, '0')}-01`; }

function currentSydney() {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date());
  return {
    year: +parts.find(p => p.type === 'year').value,
    month: +parts.find(p => p.type === 'month').value,
  };
}

const NOW = currentSydney();
const CY = NOW.year;
const CM = NOW.month;

const METRICS = [
  { key: 'net_sales',      label: 'Net Sales',      cols: ['net_sales'],      isMoney: true  },
  { key: 'gross_sales',    label: 'Gross Sales',    cols: ['gross_sales'],    isMoney: true  },
  { key: 'units',          label: 'Units Sold',     cols: ['units_sold'],     isMoney: false },
  { key: 'refunded_units', label: 'Refunded Units', cols: ['refunded_units'], isMoney: false },
];

const PRESETS = [
  { key: 'ytd',  label: 'YTD' },
  { key: '3m',   label: '3M' },
  { key: '6m',   label: '6M' },
  { key: '12m',  label: '12M' },
  { key: 'all',  label: 'All' },
];


function getVal(monthData, metric) {
  if (!monthData) return 0;
  if (metric.key === 'net_sales') {
    // Return net_sales as-is from the source table.
    // The source table (shopify_sku_monthly_revenue) should already account for refunds,
    // discounts, and returns in its net_sales column. Do NOT apply additional adjustments here.
    // If backend needs to change this, clarify the column definitions first.
    const net = Number(monthData.net_sales) || 0;
    return net;
  }
  for (const col of metric.cols) {
    if (monthData[col] != null) return Number(monthData[col]);
  }
  return 0;
}

function fmt(n, metric) {
  if (!n) return '—';
  return metric.isMoney ? `$${Math.round(n).toLocaleString()}` : Math.round(n).toLocaleString();
}

function fmtBig(n, metric) {
  if (!n) return metric.isMoney ? '$0' : '0';
  if (metric.isMoney && Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (metric.isMoney && Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return metric.isMoney ? `$${Math.round(n).toLocaleString()}` : Math.round(n).toLocaleString();
}

function Skeleton({ w = '65%', h = 11 }) {
  return <div style={{ width: w, height: h, background: '#EDECEA', borderRadius: 3, animation: 'd2c-pulse 1.4s ease-in-out infinite', display: 'inline-block' }} />;
}

function StatCard({ label, value, sub, accent = '#0F1419', loading }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10, padding: '12px 16px', minWidth: 116, flex: '1 1 116px' }}>
      <div style={{ fontSize: 9, fontFamily: MONO, color: '#9A9DA1', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {loading
        ? <><Skeleton w="70%" h={20} /><div style={{ marginTop: 4 }}><Skeleton w="50%" h={10} /></div></>
        : <>
            <div style={{ fontSize: 19, fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{value}</div>
            {sub && <div style={{ fontSize: 10, color: '#9A9DA1', fontFamily: MONO, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
          </>
      }
    </div>
  );
}

const TH = { padding: '7px 9px', fontFamily: MONO, fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9A9DA1', borderBottom: '1px solid #EDECEA', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500, background: '#FAFAF8' };
const TD = { padding: '5px 9px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: 11.5, color: '#0F1419' };

export default function D2CSales({ session } = {}) {
  // No finer-grained "d2c_write" flag exists yet — admin is the closest
  // write-permission proxy for this read-only dashboard.
  const canWrite = session?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [metric, setMetric] = useState(METRICS[0]);
  // view: 'ytd' | '3m' | '6m' | '12m' | 'all' | '2025' | '2024' | ...
  const [view, setView] = useState('ytd');
  const [sortBy, setSortBy] = useState('total');
  const [hovered, setHovered] = useState(null);
  const callId = useRef(0);

  const load = useCallback(async () => {
    const id = ++callId.current;
    setLoading(true); setError(null);
    const curPrefix = `${CY}-${String(CM).padStart(2, '0')}-01`;

    async function runQuery() {
      const { data: curData, error: curErr } = await aws.from('shopify_sku_monthly_revenue').select('*')
        .gte('month', curPrefix)
        .not('sku', 'ilike', 'TEST%');
      if (curErr) throw new Error(curErr.message);

      let hist = [], from = 0;
      while (true) {
        const { data, error: err } = await aws.from('shopify_sku_monthly_revenue')
          .select('*').lt('month', curPrefix)
          .not('sku', 'ilike', 'TEST%')
          .order('month', { ascending: false }).range(from, from + 999);
        if (err) throw new Error(err.message);
        if (!data?.length) break;
        hist = hist.concat(data);
        if (data.length < 1000) break;
        from += 1000;
      }
      return [...(curData || []), ...hist];
    }

    try {
      const result = await runQuery();
      if (id !== callId.current) return; // stale — a newer load is in flight
      setRows(result);
    } catch (e) {
      if (id !== callId.current) return;
      setError(e.message || 'Failed to load D2C data');
    } finally {
      if (id === callId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Extract years available in data — no hardcoding
  const dataYears = useMemo(() => {
    const ys = new Set();
    for (const r of rows) {
      const yr = parseInt((r.month || '').slice(0, 4));
      if (yr > 2000 && yr <= CY) ys.add(yr);
    }
    return Array.from(ys).sort((a, b) => b - a);
  }, [rows]);

  // All months present in data, sorted chronologically
  const allDataMonths = useMemo(() => {
    const seen = new Set();
    for (const r of rows) {
      const mMatch = String(r.month || '').match(/^(\d{4})-(\d{2})/);
      if (mMatch) seen.add(`${mMatch[1]}-${mMatch[2]}`);
    }
    return Array.from(seen).sort().map(ym => {
      const yr = parseInt(ym.slice(0, 4));
      const mo = parseInt(ym.slice(5, 7));
      return {
        m: mo, year: yr,
        label: `${MONTH_LABELS[mo - 1]} ${String(yr).slice(2)}`,
        key: ym + '-01',
        isFuture: false,
        isCurrent: yr === CY && mo === CM,
      };
    });
  }, [rows]);

  // Compute visible month columns from view selection
  const months = useMemo(() => {
    if (view === 'ytd') {
      return Array.from({ length: CM }, (_, i) => {
        const m = i + 1;
        return { m, year: CY, label: `${MONTH_LABELS[i]} ${String(CY).slice(2)}`, key: monthKey(CY, m), isFuture: false, isCurrent: m === CM };
      });
    }
    if (view === '3m' || view === '6m' || view === '12m') {
      const count = { '3m': 3, '6m': 6, '12m': 12 }[view];
      return Array.from({ length: count }, (_, i) => {
        let yr = CY, mo = CM - (count - 1 - i);
        while (mo < 1) { mo += 12; yr--; }
        return {
          m: mo, year: yr,
          label: `${MONTH_LABELS[mo - 1]} ${String(yr).slice(2)}`,
          key: monthKey(yr, mo), isFuture: false, isCurrent: yr === CY && mo === CM,
        };
      });
    }
    if (view === 'all') return allDataMonths;
    // Year view — full Jan-Dec calendar
    const yr = Number(view);
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      return {
        m, year: yr, label: `${MONTH_LABELS[i]} ${String(yr).slice(2)}`, key: monthKey(yr, m),
        isFuture: yr === CY && m > CM,
        isCurrent: yr === CY && m === CM,
      };
    });
  }, [view, allDataMonths]);

  // Pivot all rows → skuMap[sku].byMonth[key]
  const skuMap = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const sku = r.sku || '—';   // use SKU column only, not sku_code
      if (!map[sku]) map[sku] = { sku, title: r.product_title || r.title || null, byMonth: {} };
      // Normalise month: handles 'YYYY-MM', 'YYYY-MM-DD', 'YYYY-MM-DDThh:mm:ssZ' etc.
      const rawM = String(r.month || '');
      const mParts = rawM.match(/^(\d{4})-(\d{2})/);
      const mk = mParts ? `${mParts[1]}-${mParts[2]}-01` : null;
      if (!mk) continue;
      if (!map[sku].byMonth[mk]) map[sku].byMonth[mk] = {};
      for (const met of METRICS) {
        for (const col of met.cols) {
          if (r[col] != null) map[sku].byMonth[mk][col] = (map[sku].byMonth[mk][col] || 0) + Number(r[col]);
        }
      }
    }
    return map;
  }, [rows]);

  const activeMos = useMemo(() => months.filter(mo => !mo.isFuture), [months]);

  const skuList = useMemo(() => {
    let list = Object.values(skuMap).map(r => ({
      ...r,
      total: activeMos.reduce((s, mo) => s + getVal(r.byMonth[mo.key], metric), 0),
    }));
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter(r => r.sku.toLowerCase().includes(s) || (r.title || '').toLowerCase().includes(s));
    }
    list = list.filter(r => activeMos.some(mo => getVal(r.byMonth[mo.key], metric) > 0));
    if (sortBy === 'total') list.sort((a, b) => b.total - a.total);
    else list.sort((a, b) => a.sku.localeCompare(b.sku));
    return list;
  }, [skuMap, q, metric, sortBy, activeMos]);

  const colTotals = useMemo(() => {
    const t = {};
    for (const mo of months) {
      t[mo.key] = mo.isFuture ? 0 : skuList.reduce((s, r) => s + getVal(r.byMonth[mo.key], metric), 0);
    }
    return t;
  }, [skuList, months, metric]);

  const grandTotal = useMemo(() => activeMos.reduce((s, mo) => s + (colTotals[mo.key] || 0), 0), [activeMos, colTotals]);

  const avgPerMonth = useMemo(() => {
    const active = activeMos.filter(mo => (colTotals[mo.key] || 0) > 0).length;
    return active > 0 ? grandTotal / active : 0;
  }, [grandTotal, activeMos, colTotals]);

  const bestMonth = useMemo(() => {
    let best = null, bestVal = 0;
    for (const mo of activeMos) {
      if ((colTotals[mo.key] || 0) > bestVal) { best = mo; bestVal = colTotals[mo.key]; }
    }
    return { mo: best, val: bestVal };
  }, [activeMos, colTotals]);

  const curMonthTotal = useMemo(() => {
    const cur = months.find(mo => mo.isCurrent);
    return cur ? (colTotals[cur.key] || 0) : 0;
  }, [months, colTotals]);

  const topSku = skuList[0] || null;

  const viewLabel = {
    ytd: `YTD ${CY}`, '3m': 'Last 3 months', '6m': 'Last 6 months',
    '12m': 'Last 12 months', all: 'All time',
  }[view] || `${view}`;

  const isPreset = PRESETS.some(p => p.key === view);

  function handleDownload() {
    const columns = [
      { label: 'SKU', width: 18, value: r => r.sku || '' },
      { label: 'Title', width: 30, value: r => r.title || '' },
      ...activeMos.map(mo => ({
        label: mo.label,
        width: 11,
        numFmt: metric.isMoney ? '"A$"#,##0' : '#,##0',
        value: r => {
          const val = getVal(r.byMonth[mo.key], metric);
          return val > 0 ? Math.round(val) : '';
        },
      })),
      { label: 'Total', width: 14, numFmt: metric.isMoney ? '"A$"#,##0' : '#,##0', value: r => Math.round(r.total || 0) },
    ];
    const filename = `FON_D2C_${metric.key}_${view}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, `${metric.label} (${view})`, columns, skuList);
    logExportActivity(session, 'D2C Sales', filename);
  }

  const statCards = [
    { label: 'Period Total', value: fmtBig(grandTotal, metric), sub: viewLabel, accent: '#0F8E6F' },
    { label: 'Current Month', value: fmtBig(curMonthTotal, metric), sub: `${MONTH_LABELS[CM - 1]} ${CY}`, accent: '#0F1419' },
    { label: 'Avg / Month', value: fmtBig(avgPerMonth, metric), sub: 'active months', accent: '#0F1419' },
    { label: 'Active SKUs', value: String(skuList.length), sub: viewLabel, accent: '#0F1419' },
  ];

  return (
    <div className="app-screen" style={{ background: '#F7F6F3' }}>
      <div className="main-content">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 600, color: '#9A9DA1', textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: MONO, marginBottom: 4 }}>Direct to Consumer</div>
            <h1 style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 400, color: '#0F1419', margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>D2C Sales</h1>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: '#0F1419', borderRadius: 8, padding: 3, gap: 2 }}>
              {METRICS.map(m => (
                <button key={m.key} onClick={() => setMetric(m)} style={{
                  padding: '4px 11px', fontSize: 11, fontWeight: 500, borderRadius: 6,
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: metric.key === m.key ? '#fff' : 'transparent',
                  color: metric.key === m.key ? '#0F1419' : '#6E7278', transition: 'all 120ms',
                }}>{m.label}</button>
              ))}
            </div>
            {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {statCards.map(card => <StatCard key={card.label} loading={loading} {...card} />)}
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={q} onChange={e => setQ(e.target.value)} placeholder="Search SKU or product…"
            style={{ padding: '5px 10px', fontSize: 11, border: q.trim() ? '2px solid #00A86B' : '1px solid #E8E6E0', borderRadius: 7, background: '#fff', outline: 'none', width: 178, fontFamily: 'inherit', color: '#0F1419' }}
          />
          {q.trim() && (
            <div style={{ fontSize: 10, fontFamily: 'Monaco, monospace', color: '#00A86B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Filtered: {skuList.length} SKU{skuList.length !== 1 ? 's' : ''}
            </div>
          )}

          {/* Range presets + dynamic year pills — all in one pill group */}
          <div style={{ display: 'flex', background: '#F0EEE8', borderRadius: 7, padding: 2, gap: 1, flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => setView(p.key)} style={{
                padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
                border: 'none', cursor: 'pointer', fontFamily: MONO,
                background: view === p.key ? '#0F1419' : 'transparent',
                color: view === p.key ? '#fff' : '#9A9DA1', transition: 'all 100ms',
              }}>{p.label}</button>
            ))}
            {!loading && dataYears.length > 0 && (
              <>
                <div style={{ width: 1, background: '#D4D2CC', margin: '3px 2px', flexShrink: 0 }} />
                {dataYears.map(yr => (
                  <button key={yr} onClick={() => setView(String(yr))} style={{
                    padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
                    border: 'none', cursor: 'pointer', fontFamily: MONO,
                    background: view === String(yr) ? '#0F1419' : 'transparent',
                    color: view === String(yr) ? '#fff' : '#9A9DA1', transition: 'all 100ms',
                  }}>{yr}</button>
                ))}
              </>
            )}
          </div>


          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            padding: '5px 9px', fontSize: 11, border: '1px solid #E8E6E0', borderRadius: 7,
            background: '#fff', outline: 'none', cursor: 'pointer', fontFamily: 'inherit', color: '#6E7278',
          }}>
            <option value="total">Sort: Total ↓</option>
            <option value="sku">Sort: SKU A–Z</option>
          </select>

          <button onClick={() => load()} disabled={loading} title="Refresh" style={{
            width: 28, height: 28, border: '1px solid #E8E6E0', borderRadius: 7, background: '#fff',
            cursor: loading ? 'default' : 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#9A9DA1', opacity: loading ? 0.4 : 1, flexShrink: 0,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 9, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#DC2626' }}>Couldn't load D2C data</div>
              <div style={{ fontSize: 10, color: '#991B1B', marginTop: 2 }}>{error}</div>
            </div>
            <button onClick={load} style={{ fontSize: 11, padding: '4px 9px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#fff', color: '#DC2626', cursor: 'pointer', fontWeight: 500 }}>Retry</button>
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto', border: '1px solid #E8E6E0', borderRadius: 10, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: Math.max(680, months.length * 76 + 210), fontSize: 11.5 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'left', position: 'sticky', left: 0, zIndex: 3, minWidth: 168, borderRight: '1px solid #EDECEA' }}>
                  SKU
                </th>
                {months.map(mo => (
                  <th key={mo.key} style={{ ...TH, color: mo.isCurrent ? '#0F8E6F' : mo.isFuture ? '#D4D2CC' : undefined, minWidth: 70 }}>
                    {mo.label}
                    {mo.isCurrent && <span style={{ display: 'block', fontSize: 7, letterSpacing: '0.04em', color: '#0F8E6F', marginTop: 1 }}>PARTIAL</span>}
                  </th>
                ))}
                <th style={{ ...TH, color: '#0F1419', fontWeight: 700, borderLeft: '1px solid #EDECEA', minWidth: 84 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {loading && [...Array(10)].map((_, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F5F3F0' }}>
                  <td style={{ ...TD, textAlign: 'left', position: 'sticky', left: 0, background: '#fff', borderRight: '1px solid #EDECEA', padding: '8px 12px' }}>
                    <Skeleton w="55%" /><div style={{ marginTop: 3 }}><Skeleton w="40%" h={9} /></div>
                  </td>
                  {months.map(mo => <td key={mo.key} style={TD}><Skeleton w="58%" /></td>)}
                  <td style={{ ...TD, borderLeft: '1px solid #EDECEA' }}><Skeleton w="62%" /></td>
                </tr>
              ))}

              {!loading && skuList.length === 0 && (
                <tr>
                  <td colSpan={months.length + 2} style={{ padding: '40px 0', textAlign: 'center', color: '#9A9DA1', fontSize: 12 }}>
                    {q ? `No SKUs match "${q}"` : `No data for ${viewLabel}.`}
                  </td>
                </tr>
              )}

              {!loading && skuList.map((sku, idx) => {
                const isHov = hovered === sku.sku;
                const bg = isHov ? '#FAFAF8' : '#fff';
                return (
                  <tr key={sku.sku} style={{ borderTop: '1px solid #F5F3F0' }}
                    onMouseEnter={() => setHovered(sku.sku)} onMouseLeave={() => setHovered(null)}>
                    <td style={{ ...TD, textAlign: 'left', position: 'sticky', left: 0, background: bg, borderRight: '1px solid #EDECEA', padding: '6px 12px', transition: 'background 80ms' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {idx === 0 && <span style={{ fontSize: 7.5, background: '#0F8E6F', color: '#fff', borderRadius: 3, padding: '1px 4px', fontFamily: MONO, flexShrink: 0, letterSpacing: '0.04em' }}>TOP</span>}
                        <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 600, color: '#0F1419', letterSpacing: '0.01em' }}>{sku.sku}</span>
                      </div>
                      {sku.title && sku.title !== sku.sku && (
                        <div style={{ fontSize: 10, color: '#9A9DA1', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 158 }}>{sku.title}</div>
                      )}
                    </td>
                    {months.map(mo => {
                      const val = getVal(sku.byMonth[mo.key], metric);
                      return (
                        <td key={mo.key} style={{
                          ...TD,
                          background: mo.isCurrent ? 'rgba(15,142,111,0.04)' : mo.isFuture ? '#FAFAF8' : isHov ? '#FAFAF8' : undefined,
                          color: mo.isFuture ? '#D4D2CC' : val > 0 ? '#0F1419' : '#D4D2CC',
                          transition: 'background 80ms',
                        }}>
                          {mo.isFuture ? '—' : val > 0 ? fmt(val, metric) : '—'}
                        </td>
                      );
                    })}
                    <td style={{ ...TD, fontWeight: 600, borderLeft: '1px solid #EDECEA', background: isHov ? '#FAFAF8' : undefined, transition: 'background 80ms' }}>
                      {fmt(sku.total, metric)}
                    </td>
                  </tr>
                );
              })}

              {!loading && skuList.length > 0 && (
                <tr style={{ borderTop: '2px solid #E8E6E0', background: '#FAFAF8' }}>
                  <td style={{ ...TD, textAlign: 'left', position: 'sticky', left: 0, background: '#FAFAF8', borderRight: '1px solid #EDECEA', padding: '6px 12px', fontFamily: MONO, fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0F1419' }}>Total</td>
                  {months.map(mo => (
                    <td key={mo.key} style={{ ...TD, fontWeight: 700, background: mo.isCurrent ? 'rgba(15,142,111,0.06)' : undefined, color: mo.isFuture ? '#D4D2CC' : (colTotals[mo.key] || 0) > 0 ? '#0F1419' : '#D4D2CC' }}>
                      {mo.isFuture ? '—' : (colTotals[mo.key] || 0) > 0 ? fmt(colTotals[mo.key], metric) : '—'}
                    </td>
                  ))}
                  <td style={{ ...TD, fontWeight: 700, color: '#0F8E6F', borderLeft: '1px solid #EDECEA', fontSize: 13 }}>
                    {fmt(grandTotal, metric)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      <style>{`@keyframes d2c-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
